import React, { useState } from 'react';
import {
  Box, Drawer, AppBar, Toolbar, Typography, List, ListItem,
  ListItemButton, ListItemIcon, ListItemText, IconButton, Avatar,
  Tooltip, Divider, Chip, useTheme, useMediaQuery,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Alert, CircularProgress
} from '@mui/material';
import {
  Dashboard as DashboardIcon, People as PeopleIcon, School as SchoolIcon,
  MenuBook as SubjectsIcon, HowToReg as EnrolIcon, Assignment as AssessIcon,
  Grade as MarksIcon, TableChart as ScoreIcon, CheckCircle as CriteriaIcon,
  BarChart as ReportsIcon, Settings as SettingsIcon, Menu as MenuIcon,
  Logout as LogoutIcon, Person as PersonIcon, Lock as LockIcon,
  Group as GroupIcon, AssignmentInd as AssignmentIndIcon,
  ManageSearch as AuditIcon,
  EventNote as AttendanceIcon,
  FamilyRestroom as ParentIcon,
  AccountBalance as FeesIcon,
  Gavel as DisciplinaryIcon,
  MenuBook as LibraryIcon,
  EmojiEvents as AlumniIcon,
  Assignment as HomeworkIcon,
  Savings as BursaryIcon,
  Inventory2 as AssetIcon,
  Hotel as BoardingIcon,
  Store as ShopIcon,
  Analytics as AnalyticsIcon,
  AccountTree as NetworkIcon,
  HowToReg as AdmissionsIcon,
  MenuBook as PrepBookIcon,
  PhoneAndroid as MobileIcon,
  DirectionsBus as TransportIcon,
  Star as PerformanceIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { authAPI } from '../services/api';

const DRAWER_WIDTH = 250;

const ALL_NAV_ITEMS = [
  { label: 'Dashboard',        path: '/dashboard',        icon: <DashboardIcon />, module: 'dashboard',        teacherOnly: false },
  { label: 'Enrolment',        path: '/enrolments',       icon: <EnrolIcon />,     module: 'enrolments',       teacherOnly: false },
  { label: 'Students',         path: '/students',         icon: <PeopleIcon />,    module: 'students',         teacherOnly: false },
  { label: 'Teachers',         path: '/teachers',         icon: <PersonIcon />,    module: 'teachers',         teacherOnly: false },
  { label: 'Subjects',         path: '/subjects',         icon: <SubjectsIcon />,       module: 'subjects',         teacherOnly: false },
  { label: 'Class Assignments',path: '/class-assignments',icon: <AssignmentIndIcon />,  module: 'subjects',         teacherOnly: false },
  { label: 'Assessments',      path: '/assessments',      icon: <AssessIcon />,    module: 'assessments',      teacherOnly: false },
  { label: 'Attendance',       path: '/attendance',       icon: <AttendanceIcon />, module: 'attendance',       teacherOnly: false, classTeacherItem: true },
  { label: 'Enrol Students To Subject', path: '/class-register',   icon: <GroupIcon />,     module: 'marks',            teacherOnly: true },
  { label: 'Marks Entry',      path: '/marks',            icon: <MarksIcon />,     module: 'marks',            teacherOnly: true },
  { label: 'Scoresheet',       path: '/scoresheet',       icon: <ScoreIcon />,     module: 'scoresheet',       teacherOnly: false },
  { label: 'Passing Criteria', path: '/passing-criteria', icon: <CriteriaIcon />,  module: 'passing-criteria', teacherOnly: false },
  { label: 'Reports',          path: '/reports',          icon: <ReportsIcon />,   module: 'reports',          teacherOnly: false },
  { label: 'Settings',         path: '/settings',         icon: <SettingsIcon />,  module: 'settings',         teacherOnly: false },
  { label: 'Audit Trail',      path: '/audit-logs',       icon: <AuditIcon />,     module: 'settings',         teacherOnly: false },
  { label: 'Fee Management',    path: '/fees',              icon: <FeesIcon />,      module: 'fees',             teacherOnly: false },
  { label: 'Disciplinary',       path: '/disciplinary',      icon: <DisciplinaryIcon />, module: 'disciplinary',   teacherOnly: false },
  { label: 'Library',            path: '/library',           icon: <LibraryIcon />,     module: 'library',        teacherOnly: false },
  { label: 'Alumni',             path: '/alumni',            icon: <AlumniIcon />,      module: 'alumni',         teacherOnly: false },
  { label: 'Homework',           path: '/homework',          icon: <HomeworkIcon />,    module: 'homework',       teacherOnly: true },
  { label: 'Bursaries',          path: '/bursaries',         icon: <BursaryIcon />,     module: 'bursaries',      teacherOnly: false },
  { label: 'Assets',             path: '/assets',            icon: <AssetIcon />,       module: 'assets',         teacherOnly: false },
  { label: 'Boarding',           path: '/boarding',          icon: <BoardingIcon />,    module: 'boarding',       teacherOnly: false },
  { label: 'School Shop',        path: '/shop',              icon: <ShopIcon />,        module: 'shop',           teacherOnly: false },
  { label: 'Analytics',          path: '/analytics',         icon: <AnalyticsIcon />,   module: 'analytics',      teacherOnly: false },
  { label: 'School Network',     path: '/network',           icon: <NetworkIcon />,     module: 'network',        teacherOnly: false },
  { label: 'Admissions',         path: '/admissions',        icon: <AdmissionsIcon />,  module: 'admissions',     teacherOnly: false },
  { label: 'Prep Book',          path: '/prep-book',         icon: <PrepBookIcon />,    module: 'prep-book',      teacherOnly: true },
  { label: 'Mobile API',         path: '/mobile-api',        icon: <MobileIcon />,      module: 'settings',       teacherOnly: false },
  { label: 'Transport',          path: '/transport',         icon: <TransportIcon />,   module: 'transport',      teacherOnly: false },
  { label: 'Performance & CPD',  path: '/performance',       icon: <PerformanceIcon />, module: 'performance',    teacherOnly: false },
  { label: 'Parent Management', path: '/parent-management', icon: <ParentIcon />,    module: 'settings',         teacherOnly: false },
];

// Force password change dialog
function ChangePasswordDialog({ open }) {
  const { user } = useAuth();
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handleSubmit = async () => {
    setError('');
    if (form.new_password !== form.confirm) return setError('New passwords do not match');
    if (form.new_password.length < 6) return setError('Password must be at least 6 characters');
    setSaving(true);
    try {
      const res = await authAPI.changePassword({ current_password: form.current_password, new_password: form.new_password });
      if (res.data.success) {
        setDone(true);
        // Update local storage to clear must_change_password flag
        const stored = localStorage.getItem('sms_user');
        if (stored) {
          const u = JSON.parse(stored);
          u.must_change_password = false;
          localStorage.setItem('sms_user', JSON.stringify(u));
        }
        setTimeout(() => window.location.reload(), 1500);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error changing password');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} maxWidth="xs" fullWidth disableEscapeKeyDown>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LockIcon color="warning" /> Change Your Password
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Your account requires a password change before you can continue.
        </Alert>
        {done
          ? <Alert severity="success">Password changed! Refreshing…</Alert>
          : <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {error && <Alert severity="error">{error}</Alert>}
              <TextField label="Current Password" type="password" size="small" fullWidth
                value={form.current_password} onChange={e => setForm({ ...form, current_password: e.target.value })} />
              <TextField label="New Password" type="password" size="small" fullWidth
                value={form.new_password} onChange={e => setForm({ ...form, new_password: e.target.value })} />
              <TextField label="Confirm New Password" type="password" size="small" fullWidth
                value={form.confirm} onChange={e => setForm({ ...form, confirm: e.target.value })} />
            </Box>
        }
      </DialogContent>
      {!done && (
        <DialogActions>
          <Button variant="contained" onClick={handleSubmit} disabled={saving} fullWidth>
            {saving ? <CircularProgress size={20} /> : 'Change Password'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}

export default function Layout({ children }) {
  const { user, logout, can, schoolSlug, schoolPath } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  // Filter nav items based on permissions
  const isRestrictedTeacher = user?.role === 'teacher' && !!user?.teacher_id;
  const navItems = ALL_NAV_ITEMS.filter(item => {
    if (!can(item.module, 'view')) return false;
    if (isRestrictedTeacher) return item.teacherOnly;
    return true;
  });

  // Build slug-prefixed path and check active state
  const fullPath = (path) => schoolPath(path);
  const isActive = (path) => location.pathname === fullPath(path) ||
                              location.pathname.startsWith(fullPath(path) + '/');

  const handleNavClick = (path) => {
    navigate(fullPath(path));
    if (isMobile) setMobileOpen(false);
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        {settings.logo
          ? <Box component="img" src={`${process.env.REACT_APP_API_URL?.replace('/api','')}/uploads/${settings.logo}`}
              alt="logo" sx={{ width: 40, height: 40, borderRadius: 2, objectFit: 'cover' }} />
          : <SchoolIcon color="primary" sx={{ fontSize: 36 }} />
        }
        <Box>
          <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2} noWrap>
            {settings.school_name || 'School SMS'}
          </Typography>
          <Typography variant="caption" color="text.secondary">Management System</Typography>
        </Box>
      </Box>
      <Divider />

      <List sx={{ flex: 1, py: 1, px: 1 }}>
        {navItems.map((item) => {
          const active = isActive(item.path);
          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.3 }}>
              <ListItemButton onClick={() => handleNavClick(item.path)} selected={active}
                sx={{ borderRadius: 2, '&.Mui-selected': { bgcolor: 'primary.main', color: 'white', '& .MuiListItemIcon-root': { color: 'white' }, '&:hover': { bgcolor: 'primary.dark' } } }}>
                <ListItemIcon sx={{ minWidth: 38 }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 14, fontWeight: active ? 600 : 400 }} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Divider />
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: 14 }}>
          {user?.full_name?.charAt(0) || 'U'}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap>{user?.full_name}</Typography>
          <Chip label={user?.role} size="small" color="primary" variant="outlined" sx={{ height: 18, fontSize: 10 }} />
        </Box>
        <Tooltip title="Logout">
          <IconButton size="small" onClick={logout} color="error"><LogoutIcon fontSize="small" /></IconButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      {/* Force password change dialog */}
      <ChangePasswordDialog open={!!user?.must_change_password} />

      {isMobile && (
        <AppBar position="fixed" sx={{ zIndex: theme.zIndex.drawer + 1 }}>
          <Toolbar>
            <IconButton color="inherit" edge="start" onClick={() => setMobileOpen(!mobileOpen)} sx={{ mr: 2 }}>
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap fontWeight={700}>{settings.school_name || 'School SMS'}</Typography>
          </Toolbar>
        </AppBar>
      )}

      {!isMobile && (
        <Drawer variant="permanent" sx={{ width: DRAWER_WIDTH, flexShrink: 0, '& .MuiDrawer-paper': { width: DRAWER_WIDTH, boxSizing: 'border-box', borderRight: '1px solid', borderColor: 'divider' } }}>
          {drawerContent}
        </Drawer>
      )}

      {isMobile && (
        <Drawer variant="temporary" open={mobileOpen} onClose={() => setMobileOpen(false)}
          sx={{ '& .MuiDrawer-paper': { width: DRAWER_WIDTH } }}>
          {drawerContent}
        </Drawer>
      )}

      <Box component="main" sx={{ flex: 1, overflow: 'auto', bgcolor: 'background.default' }}>
        {isMobile && <Toolbar />}
        <Box sx={{ p: { xs: 2, md: 3 } }}>{children}</Box>
      </Box>
    </Box>
  );
}
